import { isAxiosError } from 'axios';
import { Trash } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import {
  useDeviceFrequencyLimits,
  useDeviceSettings,
  useDeviceWhitelist,
  useIntl,
  useSetDeviceWhitelist,
} from '../../hooks';
import { hzToMhz, mhzToHz } from '../../lib/frequency';
import { cn } from '../../lib/utils';
import { zodResolver } from '../../lib/zod-resolver.js';
import { Device } from '../../types';
import { buildWhitelistSchema, type DeviceWhitelistForm } from '../../validations/whitelist';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '../ui';

type Props = {
  device: Device;
};

export const toFormValues = (
  entries?: Array<{ startFrequency: number; endFrequency: number }>,
): DeviceWhitelistForm => ({
  entries: (entries ?? []).map((entry) => ({
    startFrequency: hzToMhz(entry.startFrequency),
    endFrequency: hzToMhz(entry.endFrequency),
  })),
});

export const buildWhitelistPayload = (form: DeviceWhitelistForm | undefined) =>
  (form?.entries ?? []).map((entry) => ({
    startFrequency: mhzToHz(entry.startFrequency),
    endFrequency: mhzToHz(entry.endFrequency),
  }));

export default function DeviceWhitelistPanel({ device }: Props) {
  const intl = useIntl();
  const { data, isLoading, isFetching, error, refetch } = useDeviceWhitelist(device.id);
  const { isLoading: isLoadingSettings } = useDeviceSettings(device.id);
  const { minAllowedMhz, maxAllowedMhz } = useDeviceFrequencyLimits(device.id);
  const setWhitelist = useSetDeviceWhitelist();

  const initialValues = useMemo(() => toFormValues(data?.entries ?? undefined), [data?.entries]);

  const whitelistSchema = useMemo(
    () => buildWhitelistSchema(minAllowedMhz, maxAllowedMhz),
    [minAllowedMhz, maxAllowedMhz],
  );

  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<DeviceWhitelistForm>({
    resolver: zodResolver(whitelistSchema),
    mode: 'onChange',
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
    clearErrors();
  }, [initialValues, reset, clearErrors]);

  const { fields, append, remove } = useFieldArray({ control, name: 'entries' });

  const onSubmit = handleSubmit(async (values) => {
    clearErrors();
    try {
      await setWhitelist.mutateAsync({
        id: device.id,
        entries: buildWhitelistPayload(values),
      });
    } catch (e) {
      console.error('Failed to set whitelist:', e);
      if (isAxiosError(e) && e.response?.data) {
        const data = e.response.data as {
          message?: string;
          issues?: Array<{ path?: Array<string | number>; message?: string }>;
        };

        let issuesApplied = false;

        if (Array.isArray(data.issues)) {
          data.issues.forEach((issue) => {
            if (!issue) return;
            const [rangeIndex, field] = issue.path ?? [];
            if (typeof rangeIndex !== 'number' || typeof field !== 'string') return;

            if (field === 'startFrequency' || field === 'endFrequency') {
              setError(`entries.${rangeIndex}.${field}` as const, {
                type: 'remote',
                message:
                  issue.message ??
                  data.message ??
                  intl.formatMessage({ id: 'deviceWhitelist.invalidEntry' }),
              });
              issuesApplied = true;
            }
          });
        }

        if (data.message) {
          setError('root', { type: 'remote', message: data.message });
        } else if (!issuesApplied) {
          setError('root', {
            type: 'remote',
            message: intl.formatMessage({ id: 'deviceWhitelist.setError' }),
          });
        }
        return;
      }

      setError('root', {
        type: 'remote',
        message:
          e instanceof Error ? e.message : intl.formatMessage({ id: 'deviceWhitelist.setError' }),
      });
    }
  });

  const disableInputs = setWhitelist.isPending || isFetching;
  const versionText = useMemo(() => {
    if (!data) return null;
    return intl.formatMessage(
      { id: 'deviceWhitelist.currentVersion' },
      { version: data.version, date: new Date(data.createdAt).toLocaleString() },
    );
  }, [data, intl]);

  const frequencyLimitsText = useMemo(() => {
    if (minAllowedMhz === null && maxAllowedMhz === null) {
      return intl.formatMessage({ id: 'deviceWhitelist.noLimits' });
    }
    if (minAllowedMhz !== null && maxAllowedMhz !== null) {
      return intl.formatMessage(
        { id: 'deviceWhitelist.allowedRange' },
        { min: minAllowedMhz.toFixed(2), max: maxAllowedMhz.toFixed(2) },
      );
    }
    if (minAllowedMhz !== null) {
      return intl.formatMessage(
        { id: 'deviceWhitelist.minFrequency' },
        { min: minAllowedMhz.toFixed(2) },
      );
    }
    return intl.formatMessage(
      { id: 'deviceWhitelist.maxFrequency' },
      { max: maxAllowedMhz!.toFixed(2) },
    );
  }, [minAllowedMhz, maxAllowedMhz, intl]);

  if ((isLoading && !data) || isLoadingSettings) {
    return (
      <div className="h-full flex flex-col">
        <Card className="border-0 shadow-none flex-1">
          <CardHeader>
            <CardTitle>
              {intl.formatMessage({ id: 'deviceWhitelist.title' }, { device: device.serial })}
            </CardTitle>
            <CardDescription>
              {intl.formatMessage({ id: 'deviceWhitelist.loadingDescription' })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-foreground">
              {intl.formatMessage({ id: 'deviceWhitelist.fetchingRanges' })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <Card className="border-0 shadow-none flex-1">
          <CardHeader>
            <CardTitle>
              {intl.formatMessage({ id: 'deviceWhitelist.title' }, { serial: device.serial })}
            </CardTitle>
            <CardDescription className="text-destructive">
              {intl.formatMessage(
                { id: 'deviceWhitelist.loadError' },
                {
                  error:
                    error instanceof Error
                      ? error.message
                      : intl.formatMessage({ id: 'common.unknownError' }),
                },
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {intl.formatMessage({ id: 'common.retry' })}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <Card className="border-0 shadow-none">
          <CardHeader className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>
                {intl.formatMessage({ id: 'deviceWhitelist.title' }, { serial: device.serial })}
              </CardTitle>
              <CardDescription>
                {intl.formatMessage({ id: 'deviceWhitelist.description' })}
              </CardDescription>
              {versionText && <p className="text-xs text-foreground">{versionText}</p>}
            </div>
          </CardHeader>
          <CardContent>
            {errors.root?.message && (
              <div className="text-sm text-destructive mb-4">{errors.root.message}</div>
            )}

            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-medium">
                {intl.formatMessage({ id: 'deviceWhitelist.ranges' })}
              </div>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() =>
                  append({ startFrequency: minAllowedMhz || 0, endFrequency: maxAllowedMhz || 0 })
                }
                disabled={disableInputs}
              >
                {intl.formatMessage({ id: 'deviceWhitelist.addRange' })}
              </Button>
            </div>

            <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
              {fields.length === 0 && (
                <div className="text-sm text-foreground">
                  {intl.formatMessage({ id: 'deviceWhitelist.noRanges' })}
                </div>
              )}
              {fields.map((field, idx) => {
                const entryErrors = errors.entries?.[idx];
                const startHasError = Boolean(entryErrors?.startFrequency);
                const endHasError = Boolean(entryErrors?.endFrequency);
                const rangeMessages = [
                  entryErrors?.startFrequency?.message,
                  entryErrors?.endFrequency?.message,
                ].filter(
                  (message): message is string => typeof message === 'string' && message.length > 0,
                );

                return (
                  <div key={field.id} className="grid grid-cols-5 gap-3 items-end rounded-md p-1">
                    <div className="col-span-2">
                      <Label className="block mb-1" htmlFor={`start-${idx}`}>
                        {intl.formatMessage({ id: 'deviceWhitelist.startMhz' })}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id={`start-${idx}`}
                          type="number"
                          step="any"
                          disabled={disableInputs}
                          defaultValue={field.startFrequency}
                          className={cn(
                            startHasError &&
                              'border-destructive focus-visible:ring-destructive focus-visible:ring-offset-2',
                          )}
                          {...register(`entries.${idx}.startFrequency`, { valueAsNumber: true })}
                        />
                      </div>
                      <p className="text-xs text-foreground mt-1">{frequencyLimitsText}</p>
                    </div>

                    <div className="col-span-2">
                      <Label className="block mb-1" htmlFor={`stop-${idx}`}>
                        {intl.formatMessage({ id: 'deviceWhitelist.stopMhz' })}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id={`stop-${idx}`}
                          type="number"
                          step="any"
                          disabled={disableInputs}
                          defaultValue={field.endFrequency}
                          className={cn(
                            endHasError &&
                              'border-destructive focus-visible:ring-destructive focus-visible:ring-offset-2',
                          )}
                          {...register(`entries.${idx}.endFrequency`, { valueAsNumber: true })}
                        />
                      </div>
                      <p className="text-xs text-foreground mt-1">{frequencyLimitsText}</p>
                    </div>

                    <div className="col-span-1 flex justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        type="button"
                        onClick={() => remove(idx)}
                        disabled={disableInputs}
                        title={intl.formatMessage({ id: 'deviceWhitelist.removeRange' })}
                        aria-label={intl.formatMessage({ id: 'deviceWhitelist.removeRange' })}
                      >
                        <Trash className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    {rangeMessages.length > 0 && (
                      <div className="col-span-5">
                        {rangeMessages.map((message, messageIdx) => (
                          <p key={messageIdx} className="text-sm text-destructive">
                            {message}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fixed footer with Save button */}
      <div className="shrink-0 border-t border-border bg-foreground-light p-6">
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={disableInputs || !isValid}>
            {setWhitelist.isPending
              ? intl.formatMessage({ id: 'common.saving' })
              : intl.formatMessage({ id: 'common.save' })}
          </Button>
        </div>
      </div>
    </form>
  );
}
